<template>
  <div class="content-main">
    <!-- Classify dialog -->
    <BaseModal v-model="showClassifyModal" @close="showClassifyModal = false">
      <div class="p-6" style="min-width: 420px">
        <h3 class="text-lg font-semibold mb-4">
          {{
            bulkMode
              ? `Classify ${selectedRows.length} Items`
              : `Classify: ${classifyTarget?.nkrCode}`
          }}
        </h3>
        <div class="mb-3">
          <label class="block text-sm font-medium mb-1">Classification</label>
          <select
            v-model="classifyForm.classification"
            class="w-full border rounded px-3 py-2"
          >
            <option value="">-- Select --</option>
            <option value="unrecorded_reception">Unrecorded Reception</option>
            <option value="missed_dispatch">Missed Dispatch</option>
            <option value="lost_stock">Lost Stock</option>
            <option value="damaged">Damaged</option>
            <option value="infoauto_error">Infoauto Error</option>
            <option value="count_correction">Count Correction</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div class="mb-3" v-if="!bulkMode">
          <label class="block text-sm font-medium mb-1"
            >Adjustment Quantity</label
          >
          <input
            v-model.number="classifyForm.adjustment_qty"
            type="number"
            class="w-full border rounded px-3 py-2"
          />
        </div>
        <div class="mb-3">
          <label class="block text-sm font-medium mb-1"
            >Notes <span class="text-red-500">*</span></label
          >
          <textarea
            v-model="classifyForm.notes"
            class="w-full border rounded px-3 py-2"
            rows="3"
            placeholder="Required: describe the reason for this classification"
          ></textarea>
        </div>
        <div class="flex justify-end gap-2 mt-4">
          <button
            class="px-4 py-2 border rounded hover:bg-gray-100"
            @click="showClassifyModal = false"
          >
            Cancel
          </button>
          <button
            class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            @click="applyClassification"
            :disabled="!classifyForm.classification || !classifyForm.notes"
          >
            {{ bulkMode ? "Apply to All" : "Add to Queue" }}
          </button>
        </div>
      </div>
    </BaseModal>

    <div class="content" style="width: 100%">
      <div class="header">Stock Comparison</div>

      <!-- Summary bar -->
      <div class="flex flex-wrap gap-4 px-4 py-3 bg-gray-50 border-b text-sm">
        <span
          >Total: <strong>{{ summary.total }}</strong></span
        >
        <span class="text-green-700"
          >Matches: <strong>{{ summary.matches }}</strong></span
        >
        <span class="text-red-700"
          >Mismatches: <strong>{{ summary.mismatches }}</strong></span
        >
        <span class="text-yellow-700"
          >Missing Local: <strong>{{ summary.missingLocal }}</strong></span
        >
        <span class="text-orange-700"
          >Missing Infoauto:
          <strong>{{ summary.missingInfoauto }}</strong></span
        >
        <span class="text-blue-700"
          >Classified: <strong>{{ pendingAdjustments.length }}</strong></span
        >
      </div>

      <!-- Filters -->
      <div class="flex flex-wrap gap-3 px-4 py-3 items-end">
        <div>
          <label class="block text-xs font-medium mb-1">Departamento</label>
          <select
            v-model="filters.departamento_id"
            class="border rounded px-3 py-2 text-sm"
            @change="onDepartamentoChange"
          >
            <option value="">-- Select --</option>
            <option
              v-for="d in departamentos"
              :key="d.departamentoId"
              :value="d.departamentoId"
              >{{ d.nombre }}</option
            >
          </select>
        </div>
        <div>
          <label class="block text-xs font-medium mb-1">Deposito</label>
          <select
            v-model="filters.warehouse_id"
            class="border rounded px-3 py-2 text-sm"
            @change="loadComparison"
          >
            <option value="">All</option>
            <option
              v-for="dep in depositosList"
              :key="dep.id"
              :value="dep.id"
              >{{ dep.nombre || dep.codDeposito }}</option
            >
          </select>
        </div>
        <div>
          <label class="block text-xs font-medium mb-1">Search</label>
          <input
            v-model="filters.search"
            type="text"
            class="border rounded px-3 py-2 text-sm"
            placeholder="Product code..."
            @input="debouncedLoad"
          />
        </div>
        <div class="flex items-center gap-2">
          <input
            type="checkbox"
            id="diffOnly"
            v-model="filters.differences_only"
            @change="loadComparison"
          />
          <label for="diffOnly" class="text-sm">Differences only</label>
        </div>
        <button
          class="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
          @click="syncInfoauto"
          :disabled="!filters.departamento_id || syncing"
        >
          {{ syncing ? "Syncing..." : "Sync from Infoauto" }}
        </button>
        <button
          class="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm"
          @click="initialLoad"
          :disabled="!filters.departamento_id || loadingInitial"
        >
          {{
            loadingInitial
              ? "Loading..."
              : `Initial Load: ${selectedDepartamentoNombre}`
          }}
        </button>
      </div>

      <!-- Sync progress bar -->
      <div v-if="syncProgress && syncProgress.status === 'running'" class="px-4 py-2 bg-blue-50 border-b">
        <div class="flex items-center justify-between text-sm text-blue-700 mb-1">
          <span>Syncing from Infoauto...</span>
          <span>{{ syncProgress.processed | 0 }} / {{ syncProgress.total }} ({{ syncProgress.percent }}%)</span>
        </div>
        <div class="w-full bg-blue-200 rounded-full h-2">
          <div class="bg-blue-600 h-2 rounded-full transition-all duration-500" :style="{ width: syncProgress.percent + '%' }"></div>
        </div>
      </div>

      <!-- Bulk actions -->
      <div class="flex gap-2 px-4 py-2" v-if="selectedRows.length > 0">
        <span class="text-sm text-gray-600"
          >{{ selectedRows.length }} selected</span
        >
        <button
          class="px-3 py-1 bg-blue-500 text-white rounded text-sm"
          @click="openBulkClassify"
        >
          Bulk Classify
        </button>
      </div>

      <!-- Table -->
      <div class="table">
        <table class="default-table" v-if="!loading">
          <thead>
            <tr class="table100-head">
              <th style="width: 30px">
                <input
                  type="checkbox"
                  @change="toggleSelectAll"
                  :checked="allSelected"
                />
              </th>
              <th @click="sort('nkrCode')">Product Code</th>
              <th @click="sort('localQty')">Local Qty</th>
              <th @click="sort('infoautoQty')">Infoauto Qty</th>
              <th @click="sort('difference')">Difference</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in filteredRows"
              :key="row.productId + '-' + row.warehouseId"
              :class="rowClass(row)"
            >
              <td>
                <input
                  type="checkbox"
                  :checked="isSelected(row)"
                  @change="toggleSelect(row)"
                  v-if="row.difference !== 0"
                />
              </td>
              <td>{{ row.nkrCode }}</td>
              <td>{{ row.localQty != null ? row.localQty : "—" }}</td>
              <td>{{ row.infoautoQty != null ? row.infoautoQty : "—" }}</td>
              <td>
                <span :class="differenceClass(row.difference)">
                  {{ row.difference }}
                </span>
              </td>
              <td>
                <span
                  v-if="getAdjustment(row)"
                  class="px-2 py-1 text-xs rounded bg-blue-100 text-blue-800"
                >
                  {{ getAdjustment(row).classification }}
                </span>
                <span
                  v-else-if="row.difference === 0"
                  class="px-2 py-1 text-xs rounded bg-green-100 text-green-800"
                  >match</span
                >
                <span
                  v-else
                  class="px-2 py-1 text-xs rounded bg-red-100 text-red-800"
                  >mismatch</span
                >
              </td>
              <td>
                <button
                  v-if="row.difference !== 0"
                  class="text-blue-600 hover:underline text-sm"
                  @click="openClassify(row)"
                >
                  Classify
                </button>
              </td>
            </tr>
            <tr v-if="filteredRows.length === 0">
              <td colspan="7" class="text-center py-4">
                <span v-if="!filters.departamento_id" class="text-gray-500">Select a departamento to compare stock</span>
                <span v-else-if="needsSync" class="text-orange-600 font-medium">No Infoauto data for this department — press "Sync from Infoauto" first</span>
                <span v-else-if="filters.differences_only" class="text-green-700 font-medium">All verified — no differences found</span>
                <span v-else class="text-gray-500">No data found</span>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-else class="p-8 text-center">Loading...</div>
      </div>

      <!-- Submit adjustments -->
      <div class="px-4 py-3" v-if="pendingAdjustments.length > 0">
        <button
          class="px-6 py-2 bg-orange-600 text-white rounded hover:bg-orange-700"
          @click="submitAdjustments"
          :disabled="submitting"
        >
          {{
            submitting
              ? "Submitting..."
              : `Submit ${pendingAdjustments.length} Adjustment(s)`
          }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getStockComparison,
  triggerInfoautoSync,
  triggerInitialLoad,
  getSyncProgress,
  submitDiagnosticAdjustments
} from "@/api/StockCompareService.js";
import { getDepositos } from "@/api/Infoauto.js";

export default {
  name: "StockComparePage",
  data() {
    return {
      rows: [],
      needsSync: false,
      depositosList: [],
      loading: false,
      syncing: false,
      syncProgress: null,
      syncPollInterval: null,
      submitting: false,
      loadingInitial: false,
      filters: {
        departamento_id: "",
        warehouse_id: "",
        search: "",
        differences_only: true
      },
      sortCol: "nkrCode",
      sortAsc: true,
      selectedRows: [],
      pendingAdjustments: [],
      showClassifyModal: false,
      bulkMode: false,
      classifyTarget: null,
      classifyForm: {
        classification: "",
        adjustment_qty: 0,
        notes: ""
      },
      debounceTimer: null
    };
  },
  computed: {
    departamentos() {
      return this.$store.getters["general/getDepartments"] || [];
    },
    selectedDepartamentoNombre() {
      if (!this.filters.departamento_id) return "";
      const d = this.departamentos.find(
        d => d.departamentoId === this.filters.departamento_id
      );
      return d ? d.nombre : this.filters.departamento_id;
    },
    allSelected() {
      const mismatchRows = this.filteredRows.filter(r => r.difference !== 0);
      return (
        mismatchRows.length > 0 && mismatchRows.every(r => this.isSelected(r))
      );
    },
    filteredRows() {
      let result = [...this.rows];
      if (this.filters.search) {
        const q = this.filters.search.toLowerCase();
        result = result.filter(r => r.nkrCode?.toLowerCase().includes(q));
      }
      result.sort((a, b) => {
        const aVal = a[this.sortCol];
        const bVal = b[this.sortCol];
        if (aVal < bVal) return this.sortAsc ? -1 : 1;
        if (aVal > bVal) return this.sortAsc ? 1 : -1;
        return 0;
      });
      return result;
    },
    summary() {
      const total = this.rows.length;
      const matches = this.rows.filter(r => r.difference === 0).length;
      const mismatches = this.rows.filter(
        r => r.difference !== 0 && r.localQty != null && r.infoautoQty != null
      ).length;
      const missingLocal = this.rows.filter(
        r => r.localQty == null || r.localQty === 0
      ).length;
      const missingInfoauto = this.rows.filter(
        r => r.infoautoQty == null || r.infoautoQty === 0
      ).length;
      return { total, matches, mismatches, missingLocal, missingInfoauto };
    }
  },
  async mounted() {
    await this.loadDepositos();
  },
  methods: {
    async loadDepositos() {
      try {
        this.depositosList = await getDepositos();
      } catch {
        this.depositosList = [];
      }
    },
    async onDepartamentoChange() {
      this.filters.warehouse_id = "";
      await this.loadComparison();
    },
    async loadComparison() {
      if (!this.filters.departamento_id) {
        this.rows = [];
        return;
      }
      this.loading = true;
      try {
        const params = {
          departamento_id: this.filters.departamento_id,
          filter: this.filters.differences_only ? "differences_only" : undefined
        };
        if (this.filters.warehouse_id) {
          params.warehouse_id = this.filters.warehouse_id;
        }
        const data = await getStockComparison(params);
        if (data && data.needsSync) {
          this.needsSync = true;
          this.rows = [];
        } else {
          this.needsSync = false;
          this.rows = Array.isArray(data) ? data : data.data || [];
        }
      } catch {
        this.rows = [];
      } finally {
        this.loading = false;
      }
    },
    debouncedLoad() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.loadComparison(), 300);
    },
    startSyncPolling() {
      this.syncPollInterval = setInterval(async () => {
        try {
          const progress = await getSyncProgress(this.filters.departamento_id);
          this.syncProgress = progress;
          if (progress.status === "done" || progress.status === "idle") {
            this.stopSyncPolling();
          }
        } catch {
          this.stopSyncPolling();
        }
      }, 2000);
    },
    stopSyncPolling() {
      if (this.syncPollInterval) {
        clearInterval(this.syncPollInterval);
        this.syncPollInterval = null;
      }
    },
    async syncInfoauto() {
      if (this.syncing) return;
      this.syncing = true;
      this.syncProgress = { status: "running", total: 0, processed: 0, percent: 0 };
      this.startSyncPolling();
      try {
        await triggerInfoautoSync(this.filters.departamento_id);
        this.syncProgress = null;
        await this.loadComparison();
      } finally {
        this.stopSyncPolling();
        this.syncing = false;
      }
    },
    async initialLoad() {
      if (
        !confirm(
          "This will fetch ALL products from Infoauto and seed the inventory table. This may take several minutes. Continue?"
        )
      ) {
        return;
      }
      this.loadingInitial = true;
      this.syncProgress = { status: "running", total: 0, processed: 0, percent: 0 };

      // Fire without awaiting — the proxy (Cloudflare) may time out (524) but the
      // server keeps running thanks to ignore_user_abort(true) + set_time_limit(0).
      triggerInitialLoad(this.filters.departamento_id).catch(() => {});

      // Drive completion via polling instead of awaiting the HTTP response.
      const departamentoId = this.filters.departamento_id;
      try {
        await new Promise((resolve, reject) => {
          this.syncPollInterval = setInterval(async () => {
            try {
              const progress = await getSyncProgress(departamentoId);
              this.syncProgress = progress;
              if (progress.status === "done") {
                clearInterval(this.syncPollInterval);
                this.syncPollInterval = null;
                resolve();
              } else if (progress.status === "error") {
                clearInterval(this.syncPollInterval);
                this.syncPollInterval = null;
                reject(new Error("error"));
              }
            } catch {
              clearInterval(this.syncPollInterval);
              this.syncPollInterval = null;
              reject(new Error("poll_failed"));
            }
          }, 2000);
        });
        const total = this.syncProgress ? this.syncProgress.total : 0;
        this.syncProgress = null;
        alert(`Initial load complete: ${total} products processed.`);
        await this.loadComparison();
      } catch {
        alert("Initial load failed. Check server logs.");
      } finally {
        this.loadingInitial = false;
      }
    },
    sort(col) {
      if (this.sortCol === col) {
        this.sortAsc = !this.sortAsc;
      } else {
        this.sortCol = col;
        this.sortAsc = true;
      }
    },
    rowKey(row) {
      return `${row.productId}-${row.warehouseId}`;
    },
    isSelected(row) {
      return this.selectedRows.some(r => this.rowKey(r) === this.rowKey(row));
    },
    toggleSelect(row) {
      if (this.isSelected(row)) {
        this.selectedRows = this.selectedRows.filter(
          r => this.rowKey(r) !== this.rowKey(row)
        );
      } else {
        this.selectedRows.push(row);
      }
    },
    toggleSelectAll() {
      const mismatchRows = this.filteredRows.filter(r => r.difference !== 0);
      if (this.allSelected) {
        this.selectedRows = [];
      } else {
        this.selectedRows = [...mismatchRows];
      }
    },
    rowClass(row) {
      if (row.difference === 0) return "bg-green-50";
      if (row.localQty == null || row.localQty === 0) return "bg-yellow-50";
      return "bg-red-50";
    },
    differenceClass(diff) {
      if (diff === 0) return "text-green-700 font-semibold";
      if (diff > 0) return "text-blue-700 font-semibold";
      return "text-red-700 font-semibold";
    },
    getAdjustment(row) {
      return this.pendingAdjustments.find(
        a =>
          a.product_id === row.productId && a.warehouse_id === row.warehouseId
      );
    },
    openClassify(row) {
      this.bulkMode = false;
      this.classifyTarget = row;
      this.classifyForm = {
        classification: "",
        adjustment_qty: row.difference || 0,
        notes: ""
      };
      this.showClassifyModal = true;
    },
    openBulkClassify() {
      this.bulkMode = true;
      this.classifyTarget = null;
      this.classifyForm = { classification: "", adjustment_qty: 0, notes: "" };
      this.showClassifyModal = true;
    },
    applyClassification() {
      if (this.bulkMode) {
        for (const row of this.selectedRows) {
          const existing = this.pendingAdjustments.findIndex(
            a =>
              a.product_id === row.productId &&
              a.warehouse_id === row.warehouseId
          );
          const entry = {
            product_id: row.productId,
            warehouse_id: row.warehouseId,
            classification: this.classifyForm.classification,
            adjustment_qty: row.difference || 0,
            notes: this.classifyForm.notes
          };
          if (existing >= 0) {
            this.pendingAdjustments.splice(existing, 1, entry);
          } else {
            this.pendingAdjustments.push(entry);
          }
        }
        this.selectedRows = [];
      } else {
        const row = this.classifyTarget;
        const existing = this.pendingAdjustments.findIndex(
          a =>
            a.product_id === row.productId && a.warehouse_id === row.warehouseId
        );
        const entry = {
          product_id: row.productId,
          warehouse_id: row.warehouseId,
          classification: this.classifyForm.classification,
          adjustment_qty: this.classifyForm.adjustment_qty,
          notes: this.classifyForm.notes
        };
        if (existing >= 0) {
          this.pendingAdjustments.splice(existing, 1, entry);
        } else {
          this.pendingAdjustments.push(entry);
        }
      }
      this.showClassifyModal = false;
    },
    async submitAdjustments() {
      this.submitting = true;
      try {
        await submitDiagnosticAdjustments({
          departamento_id: this.filters.departamento_id,
          warehouse_id: this.filters.warehouse_id || undefined,
          adjustments: this.pendingAdjustments
        });
        this.pendingAdjustments = [];
        await this.loadComparison();
      } catch {
        alert("Failed to submit adjustments");
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>
